import { Vector3 } from 'three';
import { terrainHeight } from '../world/heightfield';

/**
 * Balistique des obus. Pas de moteur physique, pas de Raycaster : une
 * trajectoire sous acceleration constante a une solution analytique, et
 * c'est elle qu'on utilise partout.
 *
 * Le meme couple de fonctions sert a trois choses, ce qui garantit qu'elles
 * ne peuvent pas diverger :
 *   - l'obus en vol (entities/Shells.ts) ;
 *   - la trajectoire predite affichee dans la lunette (ui + Shells) ;
 *   - les tests automatises (scripts/screenshot.mjs).
 *
 * cf. doc/04-balistique.md
 */

/** Vitesse initiale de l'obus, en unites par seconde (1 unite = 1 metre). */
export const MUZZLE_SPEED = 82;

/**
 * Gravite du jeu. Elle n'est pas a 9,81 : a cette valeur, la portee maximale
 * serait de 82^2 / 9,81 = 686 m, soit plus que le champ de tir entier, et
 * tous les tirs seraient tendus. A 24 m/s^2 la portee tombe a 280 m, l'ordre
 * de grandeur du terrain, et la hausse redevient une decision de jeu.
 * cf. doc/04-balistique.md
 */
export const GRAVITY = 24;

/** Portee maximale sur terrain plat, atteinte a 45 degres : v^2 / g. */
export const MAX_RANGE = (MUZZLE_SPEED * MUZZLE_SPEED) / GRAVITY;

/** Bornes de la hausse du canon, en radians. */
export const PITCH_MIN = -0.14;
export const PITCH_MAX = 0.62;

/**
 * Position exacte a l'instant t d'un projectile lance depuis `origin` a la
 * vitesse `velocity`.
 *
 * C'est la solution fermee de p'' = -g : aucune integration, donc aucune
 * derive. Un Euler explicite par frame donnerait une portee dependante du
 * framerate -- et la trajectoire affichee ne passerait pas la ou l'obus
 * passe vraiment. cf. doc/04-balistique.md
 */
export function positionAt(origin: Vector3, velocity: Vector3, t: number, out: Vector3): Vector3 {
  return out.set(
    origin.x + velocity.x * t,
    origin.y + velocity.y * t - 0.5 * GRAVITY * t * t,
    origin.z + velocity.z * t,
  );
}

/** Vitesse a l'instant t : seule la composante verticale change. */
export function velocityAt(velocity: Vector3, t: number, out: Vector3): Vector3 {
  return out.set(velocity.x, velocity.y - GRAVITY * t, velocity.z);
}

/** Direction de tir unitaire pour un gisement et une hausse donnes. */
export function aimDirection(yaw: number, pitch: number, out = new Vector3()): Vector3 {
  const cp = Math.cos(pitch);
  // yaw = 0 regarde vers +z, comme le reste du projet.
  return out.set(Math.sin(yaw) * cp, Math.sin(pitch), Math.cos(yaw) * cp);
}

/**
 * Hausse a donner pour toucher un point situe a `range` metres devant et
 * `rise` metres au-dessus de la bouche. Renvoie null si le point est hors de
 * portee.
 *
 * On resout l'equation de la trajectoire en x :
 *     rise = range * tan(t) - g * range^2 / (2 v^2 cos^2(t))
 * qui, posee en tan(t), est un polynome du second degre. Des deux racines on
 * garde la plus basse : c'est le tir tendu, celui qui arrive le plus vite.
 */
export function launchPitch(range: number, rise: number, high = false): number | null {
  const v2 = MUZZLE_SPEED * MUZZLE_SPEED;
  const disc = v2 * v2 - GRAVITY * (GRAVITY * range * range + 2 * rise * v2);
  if (disc < 0) return null;
  const root = Math.sqrt(disc);
  return Math.atan((v2 + (high ? root : -root)) / (GRAVITY * range));
}

/** Portee sur terrain plat pour une hausse donnee : v^2 sin(2t) / g. */
export function flatRange(pitch: number): number {
  return ((MUZZLE_SPEED * MUZZLE_SPEED) / GRAVITY) * Math.sin(2 * pitch);
}

export interface Impact {
  /** Point d'impact, sur le sol ou au bout du temps de vol accorde. */
  point: Vector3;
  /** Instant de l'impact, en secondes depuis le depart du coup. */
  time: number;
  /** false quand l'obus a epuise son temps de vol sans rien toucher. */
  hitGround: boolean;
}

/** Duree de vie maximale d'un obus, en secondes. */
export const MAX_FLIGHT = 14;

/** Longueur maximale d'un pas de marche, en metres. */
const STEP_LEN = 2.2;

/**
 * Marche le long de l'arc jusqu'a la premiere penetration du sol.
 *
 * Le pas est exprime en *distance* et non en temps : pres de l'apogee l'obus
 * est lent et quelques grands pas suffisent, alors qu'a l'arrivee il descend
 * vite et il faut resserrer. A pas de temps constant, on raterait le sol en
 * passant au travers d'une levre de cratere.
 *
 * Une fois l'intervalle encadre, huit dichotomies ramenent l'erreur a
 * 2,2 / 256 soit moins d'un centimetre : assez pour poser une trace au sol
 * sans la voir flotter.
 */
export function marchToImpact(
  origin: Vector3,
  velocity: Vector3,
  out: Impact = { point: new Vector3(), time: 0, hitGround: false },
): Impact {
  const probe = new Vector3();
  const speed = velocity.length() || 1;
  let t = 0;
  let prevT = 0;

  // Si on part deja sous le sol (la bouche enterree dans un merlon), l'impact
  // est immediat : sans ce cas, la premiere comparaison le manque.
  positionAt(origin, velocity, 0, probe);
  if (probe.y <= terrainHeight(probe.x, probe.z)) {
    out.point.copy(probe);
    out.time = 0;
    out.hitGround = true;
    return out;
  }

  while (t < MAX_FLIGHT) {
    // Vitesse instantanee : |v| varie d'un facteur 3 entre le depart et la
    // chute terminale, le pas de temps doit suivre.
    const vy = velocity.y - GRAVITY * t;
    const inst = Math.hypot(velocity.x, vy, velocity.z) || speed;
    prevT = t;
    t = Math.min(t + STEP_LEN / inst, MAX_FLIGHT);
    positionAt(origin, velocity, t, probe);
    if (probe.y <= terrainHeight(probe.x, probe.z)) {
      let lo = prevT;
      let hi = t;
      for (let i = 0; i < 8; i++) {
        const mid = (lo + hi) * 0.5;
        positionAt(origin, velocity, mid, probe);
        if (probe.y <= terrainHeight(probe.x, probe.z)) hi = mid;
        else lo = mid;
      }
      positionAt(origin, velocity, hi, out.point);
      out.time = hi;
      out.hitGround = true;
      return out;
    }
  }

  positionAt(origin, velocity, MAX_FLIGHT, out.point);
  out.time = MAX_FLIGHT;
  out.hitGround = false;
  return out;
}

/**
 * Echantillonne l'arc en `count` points jusqu'a `tEnd`, pour tracer la
 * trajectoire predite. L'espacement est uniforme en temps : les points se
 * resserrent naturellement a l'apogee, ou la courbure est la plus forte.
 */
export function sampleArc(
  origin: Vector3,
  velocity: Vector3,
  tEnd: number,
  count: number,
  out: Float32Array,
): void {
  const p = new Vector3();
  for (let i = 0; i < count; i++) {
    positionAt(origin, velocity, (i / (count - 1)) * tEnd, p);
    out[i * 3] = p.x;
    out[i * 3 + 1] = p.y;
    out[i * 3 + 2] = p.z;
  }
}

/**
 * Distance du point le plus proche d'un segment au centre d'une sphere.
 *
 * C'est le test de collision des obus : entre deux frames l'obus avance de
 * plusieurs metres, un test ponctuel le ferait traverser les cibles. On
 * compare donc le segment parcouru, pas la position.
 */
export function segmentSphereHit(
  a: Vector3,
  b: Vector3,
  center: Vector3,
  radius: number,
): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const fx = a.x - center.x;
  const fy = a.y - center.y;
  const fz = a.z - center.z;
  const dd = dx * dx + dy * dy + dz * dz;
  // Segment degenere : on retombe sur un test point / sphere.
  let s = dd > 1e-9 ? -(fx * dx + fy * dy + fz * dz) / dd : 0;
  s = s < 0 ? 0 : s > 1 ? 1 : s;
  const cx = fx + dx * s;
  const cy = fy + dy * s;
  const cz = fz + dz * s;
  return cx * cx + cy * cy + cz * cz <= radius * radius;
}
